import React, { useEffect, useMemo, useState, CSSProperties } from 'react'
import Card from '../card'
import {Tooltip, Skeleton, Descriptions } from 'antd'
import { safeParseJSON } from '../util/string'
import useRequest from '../hooks/useRequest'
import classnames from 'classnames'
import Tag from '../tag'
import { query } from '../services/resource'

export declare type Breakpoint = 'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs'

export declare type DataColumn = {
  dataIndex: string
  title: string
  type?: string
  span?: number
}

export declare type DataColumns = Array<DataColumn>

export interface DetailProps {
  // Query 查询数据的 body 参数，比如查询 { id: 123 }
  queryBody?: any
  // Query 对应的请求接口
  queryPath?: string
  // Query 对应的请求方法
  queryMethod?: string
  // 是否采用 Card 外框样式
  card?: boolean
  // Detail 的 Title
  title?: string
  // 标题栏右侧
  extra?: React.ReactNode
  // Card 底部
  footer?: React.ReactNode
  column?: number | Partial<Record<Breakpoint, number>>
  // 自定义字段渲染
  customRenders?: any
  // 描述布局
  layout?: 'horizontal' | 'vertical' | undefined
  // 是否展示边框
  bordered?: boolean
  // 是否展示 Item 冒号
  colon?: boolean
  // 自定义数据
  dataSource?: any
  // 自定义数据列
  dataColumns?: DataColumns
  // Descriptions 的 size
  size?: 'default' | 'small' | 'middle' | undefined
  className?: string
  clearDefaultClassName?: boolean
  // 自定义内容样式
  contentStyle?: CSSProperties
  // 自定义标签样式
  labelStyle?: CSSProperties
}

const EmptyItem = <span style={{ color: '#1C1F2373' }}>--</span>

const getRender = (field: any, val: any) => {
  if (field.type === 'timestamp') {
    if (!val || val === '0') return EmptyItem

    const date = new Date(val * 1000)
    return (
      <Tooltip title={date.toISOString()}>
        <div className="whitespace-nowrap">{date.toLocaleString()}</div>
      </Tooltip>
    )
  } else if (field.type === 'email') {
    if (!val) return EmptyItem

    return (
      <a href={`mailto: ${val}`} title={val}>
        {val}
      </a>
    )
  } else if (field.type === 'url') {
    if (!val) return EmptyItem

    return (
      <a href={val} target="_blank" title={val} rel="noreferrer">
        {val}
      </a>
    )
  } else if (field.type === 'tags') {
    if (!val) return EmptyItem

    return <Tag tags={val} />
  } else if (field.type === 'ip_addr') {
    if (!val) return EmptyItem

    return <div className="whitespace-nowrap">{val}</div>
  } else if (field.type === 'text') {
    if (!val) {
      return <>--</>
    }

    return (
      <pre className="w-full overflow-hidden break-words " style={{ whiteSpace: 'break-spaces' }}>
        <code>{val}</code>
      </pre>
    )
  } else {
    if (!val) return EmptyItem

    return <>{val}</>
  }
}

export type DetailRef = {
  /**
   * 重新加载当前页
   */
  reload: () => void
}

const Detail: React.ForwardRefRenderFunction<DetailRef, any> = (
  {
    queryBody,
    queryPath,
    queryMethod,
    card = true,
    title,
    extra,
    footer,
    column = { md: 1, lg: 2 },
    customRenders = {},
    layout,
    bordered = true,
    colon,
    dataSource,
    dataColumns,
    size = 'middle',
    className,
    clearDefaultClassName,
    contentStyle,
    labelStyle,
  }: DetailProps,
  ref
) => {
  const [queryData, setQueryData] = useState(null)
  const { run: runQuery, loading: queryLoading } = useRequest(query, {
    manual: true,
  })

  const fetchData = async () => {
    if (!queryPath || !queryMethod) return null

    const res = await runQuery({ body: queryBody })
    const result = safeParseJSON(res.body, [])[0]
    setQueryData({ ...result })
  }

  useEffect(() => {
    if (!!queryBody) return undefined

    fetchData()
  }, [queryBody])

  React.useImperativeHandle(
    ref,
    () => ({
      reload: fetchData,
    }),
    [fetchData]
  )


  const renderDescriptionItems = useMemo(() => {
    if (!!queryData && !!dataColumns) {
      return dataColumns.map((item: DataColumn) => {
        return (
          <Descriptions.Item
            label={item.title}
            key={item.dataIndex}
            span={item.span}
            contentStyle={contentStyle}
            labelStyle={labelStyle}
          >
            {customRenders[item.dataIndex]
              ? customRenders[item.dataIndex]?.(queryData[item.dataIndex], queryData)
              : getRender(item, queryData[item.dataIndex])}
          </Descriptions.Item>
        )
      })
    } else if (!!dataSource && !!dataColumns) {
      return dataColumns.map((item: DataColumn) => {
        return (
          <Descriptions.Item label={item.title} key={item.dataIndex} span={item.span}>
            {customRenders[item.dataIndex]
              ? customRenders[item.dataIndex]?.(dataSource[item.dataIndex], dataSource)
              : getRender(item, dataSource[item.dataIndex])}
          </Descriptions.Item>
        )
      })
    } else {
      return []
    }
  }, [queryData, dataSource, dataColumns, contentStyle, labelStyle])

  const Content = (
    <Skeleton loading={queryLoading}>
      <div
        className={classnames({
          'mx-auto max-w-7xl': !clearDefaultClassName,
          [className!]: !!className,
        })}
      >
        <Descriptions
          title={title}
          extra={extra && <div className="space-x-2">{extra}</div>}
          bordered={bordered}
          column={column}
          colon={colon}
          size={size}
          labelStyle={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}
          contentStyle={{ verticalAlign: 'top' }}
          layout={layout}
        >
          {renderDescriptionItems}
        </Descriptions>

        {footer && <div className="mt-5 space-x-2">{footer}</div>}
      </div>
    </Skeleton>
  )

  return card ? (
    <Card bordered={false} className={'mb-5'}>
      {Content}
    </Card>
  ) : (
    <div>{Content}</div>
  )
}

export default React.forwardRef(Detail)
